"use client";

import React, { useState, useRef } from "react";
import Link from "next/link";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "../components/Header/Header.tsx";
import {
  setEmailInLocalStorage,
  setTokenInLocalStorage,
  setUserDataInLocalStorage,
} from "../utils/localStorage.ts";
import { Button } from "@/components/ui/button";
import "./style.css";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import "./page.css";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm, SubmitHandler } from "react-hook-form";
import { useRouter } from "next/navigation";
import { Eye, EyeOff } from "lucide-react";
import { setToken } from "../lib/token.ts";

import { useSelector, useDispatch } from "react-redux";
import { login, setUser, logout } from "../lib/slices/loginSlice";
import { AppDispatch, RootState } from "../lib/store/store";

interface HomeProps {
  searchParams: { [key: string]: string };
}

type FormData = {
  email: string;
  password: string;
};

const formSchema = z
  .object({
    email: z
      .string()
      .min(1, { message: "Email is required." })
      .min(5, {
        message: "Email must be at least 5 characters.",
      })
      .email({
        message: "Invalid email address.",
      }),
    password: z
      .string()
      .min(1, {
        message: "Password is required.",
      })
      .min(6, {
        message: "Password must be at least 6 characters.",
      }),
  })
  .required();

const Home: React.FC<HomeProps> = () => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { loading, error, user } = useSelector(
    (state: RootState) => state.login
  );

  const userRef = useRef(false);

  const dispatch = useDispatch<AppDispatch>();

  // React.useEffect(() => {
  //   if (userRef.current === false) {
  //     dispatch(fetchUsers());
  //   }

  //   return () => {
  //     userRef.current = true;
  //   };
  // }, []);

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev); // Toggle password visibility
  };

  const formRef = useRef<HTMLFormElement>(null);
  const router = useRouter();

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const resultAction = await dispatch(login(data));
    if (login.fulfilled.match(resultAction)) {
      const responseData = resultAction.payload;
      dispatch(setUser(responseData));
      router.push("/dashboard");
      setEmailInLocalStorage(responseData.data.email);
      setTokenInLocalStorage(responseData.token);
      setUserDataInLocalStorage(responseData);
      toast.success(responseData.message, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } else {
      toast.info(resultAction.payload as string, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };

  const handleOnClick = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <>
      <Header />
      <div className="m-4">
        <ToastContainer />
        <h2 className="text-gradient leading-normal text-center text-3xl font-bold mb-4 mt-12">
          Log in
        </h2>
        <div className="lg:w-2/4 md:w-2/3 w-full lg:m-auto md:m-auto lg:min-w-96 md:min-w-96 lg:p-1 md:p-1 bg-gray-200 rounded-3xl lg:bg-gradient-to-r lg:from-[#BD00FF] lg:to-[#3A4EFF] md:bg-gradient-to-r md:from-[#BD00FF] md:to-[#3A4EFF]  flex flex-col justify-between mobileRes">
          <div className="bg-white lg:p-8 md:p-8 rounded-3xl">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-8"
                ref={formRef}
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold text-lg">Email</FormLabel>
                      <FormControl>
                        <Input
                          // type="email"
                          className="border-[#B3B3B3] border-2 py-6 custom-placeholder"
                          placeholder="enter your email"
                          style={{ outline: "none" }}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold text-lg">
                        Password
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            className="border-[#B3B3B3] bg-[#FBFBFB] border-2 py-6 custom-placeholder"
                            placeholder="enter your password"
                            {...field}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 flex items-center px-3 focus:outline-none"
                            onClick={togglePasswordVisibility}
                          >
                            {showPassword ? (
                              // <EyeOffIcon className="h-6 w-6 text-gray-500" />
                              <EyeOff className="h-6 w-6" />
                            ) : (
                              // <EyeIcon className="h-6 w-6 text-gray-500" />
                              <Eye className="h-6 w-6" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end rmvMarginTop">
                  <Link
                    href="/forgotpassword"
                    className="text-gradient  font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative lg:block md:block hidden ">
                  <div className="lg:hidden md:hidden absolute inset-x-0 top-0 h-2 w-full bg-gradient-to-b from-transparent to-black drop-shadow-xl "></div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#BD00FF] to-[#3A4EFF] submitBtn text-lg"
                  >
                    Submit
                  </Button>
                  <p className="rmvMarginTop text-center text-sm">
                    Don’t have an account?{" "}
                    <Link className="text-gradient" href="/signup">
                      Sign Up
                    </Link>
                  </p>
                </div>
              </form>
            </Form>
          </div>
          <div className="relative lg:hidden md:hidden block bg-white">
            <div className="lg:hidden md:hidden absolute inset-x-0 top-0 h-2 w-full bg-gradient-to-b from-transparent to-black drop-shadow-xl"></div>
            <Button
              type="button"
              onClick={handleOnClick}
              className="w-full bg-gradient-to-r from-[#BD00FF] to-[#3A4EFF] submitBtn text-base"
            >
              Submit
            </Button>
            <p className="rmvMarginTop text-center text-sm">
              Don’t have an account?{" "}
              <Link className="text-gradient" href="/signup">
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
